import { DotsVerticalIcon } from "@heroicons/react/outline";
import { CheckIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import { ContextMenu } from "../../ui/ContextMenu";
import { DropdownMenu } from "../../ui/DropdownMenu";
import { OverflowDisplayTooltip } from "../../ui/Tooltip/OverflowDisplayTooltip";
import { useDragDrop } from "../DragAndDrop/DragDropProvider";
import DraggableItem from "../DragAndDrop/DraggableItem";
import { CHILD_CONTENT_ENTER_ANIMATION_CLASSNAME } from "../hooks/useInitialEnterAnimation";
import { useUnifiedRegistry } from "../unified-registry-context";
import { getEntityName } from "../utils";

import ExpandButton from "./ExpandButton";
import ExpandedContent from "./ExpandedContent";
import { useListItemLogic } from "./hooks/useListItemLogic";
import TreeLines from "./TreeLines";
import { ListItemProps } from "./types";
import { TREE_CONSTANTS } from "./utils";

import EntityFavoriteStar from "@/components/EntityFavoriteStar";
import { EntityIcon } from "@/components/ui/icons";
import UserAttribution from "@/components/UserAttribution";
import { cn } from "@/lib/utils";
import { EntityType } from "@/queries/folder-entities/types";

const ListItem: React.FC<ListItemProps> = observer(
  ({
    item,
    isCompact = false,
    depth = 0,
    isLastChild = false,
    droppableDisabled = false,
    onItemClick,
    shouldAnimateIn = false,
  }) => {
    const registry = useUnifiedRegistry();
    const { activeItem, draggedOverFolderId } = useDragDrop();
    const {
      selected,
      isExpanded,
      isExpandable,
      link,
      contextMenuItems,
      dropdownMenuItems,
      formatDate,
      handleClick,
      handleExpandToggle,
      prefetchSmartTableNavigation,
    } = useListItemLogic(item, onItemClick);

    const isDisabled = registry.isDisabled(item);
    const isThisItemActive =
      !!activeItem && activeItem.id === item.id && activeItem.type === item.type;
    const showRootHighlight = draggedOverFolderId === -1 && depth === 0 && !isThisItemActive;

    const getItemPath = (listItem: ListItemProps["item"]) => {
      if (!listItem.path || listItem.path.length === 0) {
        return "Home";
      }

      if (listItem.type === EntityType.FOLDER) {
        const pathWithoutSelf = listItem.path.slice(0, listItem.path.length - 1);
        return pathWithoutSelf.length > 0 ? pathWithoutSelf.join("/") : "Home";
      }
      return listItem.path.join("/");
    };

    const commonStyles = {
      paddingLeft: `${12 + depth * TREE_CONSTANTS.INDENT_WIDTH}px`,
      paddingRight: "16px",
    };

    let itemNameClassName = "text-gray-900 group-hover:text-blue-600";
    if (isDisabled) {
      itemNameClassName = "text-gray-500";
    } else if (selected) {
      itemNameClassName = "text-blue-700";
    }

    const commonClassName = cn(
      "group flex items-center border-b border-gray-100 py-2 transition-colors",
      isDisabled ? "bg-gray-50 opacity-60 cursor-not-allowed" : "bg-white hover:bg-gray-50",
      selected && !isDisabled && "bg-blue-50 hover:bg-blue-50",
      shouldAnimateIn && CHILD_CONTENT_ENTER_ANIMATION_CLASSNAME,
    );

    const trailingActions = (
      <div className="flex w-14 flex-shrink-0 items-center justify-end gap-0.5">
        {selected && <CheckIcon className="mr-1 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden />}
        {!isDisabled && !registry.modal ? (
          <EntityFavoriteStar entityType={item.type} entityId={item.id} />
        ) : null}
        {!isDisabled ? (
          <div className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            <DropdownMenu
              icon={<DotsVerticalIcon className="h-4 w-4" />}
              menuItems={dropdownMenuItems}
              item={item}
              triggerClassName="rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            />
          </div>
        ) : (
          <div className="w-5" />
        )}
      </div>
    );

    return (
      <DraggableItem
        item={item}
        className="w-full"
        droppableDisabled={droppableDisabled}
        disabled={isDisabled}
      >
        <div
          className={cn(
            "relative transition-all duration-200 ease-out",
            showRootHighlight && "scale-[0.95]",
          )}
        >
          {showRootHighlight && (
            <div className="pointer-events-none absolute inset-0 z-10 rounded bg-blue-500 bg-opacity-10" />
          )}
          <ContextMenu
            modal={registry.modal}
            contextMenuItems={isDisabled ? [] : contextMenuItems}
            data={item}
            isDisabled={isDisabled}
          >
            <div className="relative">
              <TreeLines depth={depth} />
              {onItemClick ? (
                <div
                  onClick={isDisabled ? undefined : handleClick}
                  className={cn(commonClassName, !isDisabled && "cursor-pointer")}
                  style={commonStyles}
                >
                  <ExpandButton
                    isExpanded={isExpanded}
                    onToggle={handleExpandToggle}
                    isExpandable={isExpandable}
                  />

                  <div
                    className={`flex flex-1 items-center gap-2.5 ${
                      registry.modal ? "min-w-0" : "min-w-[200px]"
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <EntityIcon type={item.type} />
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <OverflowDisplayTooltip
                        content={item.name}
                        className={cn("block text-sm font-medium", itemNameClassName)}
                      >
                        {item.name}
                      </OverflowDisplayTooltip>
                    </div>
                  </div>

                  {registry.isFlattened && !registry.modal && (
                    <div className="flex-shrink-0" style={{ width: "192px" }}>
                      <div className="overflow-hidden px-4">
                        <OverflowDisplayTooltip
                          content={getItemPath(item)}
                          className="block text-xs text-gray-500"
                        >
                          {getItemPath(item)}
                        </OverflowDisplayTooltip>
                      </div>
                    </div>
                  )}

                  {!registry.modal && (
                    <div className="flex-shrink-0" style={{ width: "128px" }}>
                      <div className="overflow-hidden px-4">
                        <OverflowDisplayTooltip
                          content={getEntityName(item.type)}
                          className="block text-xs text-gray-700"
                        >
                          {getEntityName(item.type)}
                        </OverflowDisplayTooltip>
                      </div>
                    </div>
                  )}

                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="overflow-hidden px-4">
                      <UserAttribution email={item.created_by_email} />
                    </div>
                  </div>

                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="overflow-hidden px-4">
                      <OverflowDisplayTooltip
                        content={formatDate(item.created_at)}
                        className="block text-xs text-gray-500"
                      >
                        {formatDate(item.created_at)}
                      </OverflowDisplayTooltip>
                    </div>
                  </div>

                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="overflow-hidden px-4">
                      <OverflowDisplayTooltip
                        content={formatDate(item.updated_at || item.created_at)}
                        className="block text-xs text-gray-500"
                      >
                        {formatDate(item.updated_at || item.created_at)}
                      </OverflowDisplayTooltip>
                    </div>
                  </div>

                  {trailingActions}
                </div>
              ) : (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <Link
                  to={isDisabled ? "#" : link}
                  onClick={isDisabled ? e => e.preventDefault() : handleClick}
                  onPointerEnter={prefetchSmartTableNavigation}
                  onFocus={prefetchSmartTableNavigation}
                  onMouseDown={prefetchSmartTableNavigation}
                  className={commonClassName}
                  style={commonStyles}
                >
                  <ExpandButton
                    isExpanded={isExpanded}
                    onToggle={handleExpandToggle}
                    isExpandable={isExpandable}
                  />

                  <div
                    className={`flex flex-1 items-center gap-2.5 ${
                      registry.modal ? "min-w-0" : "min-w-[200px]"
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <EntityIcon type={item.type} />
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <OverflowDisplayTooltip
                        content={item.name}
                        className={cn("block text-sm font-medium", itemNameClassName)}
                      >
                        {item.name}
                      </OverflowDisplayTooltip>
                    </div>
                  </div>

                  {registry.isFlattened && !registry.modal && (
                    <div className="flex-shrink-0" style={{ width: "192px" }}>
                      <div className="overflow-hidden px-4">
                        <OverflowDisplayTooltip
                          content={getItemPath(item)}
                          className="block text-xs text-gray-500"
                        >
                          {getItemPath(item)}
                        </OverflowDisplayTooltip>
                      </div>
                    </div>
                  )}

                  {!registry.modal && (
                    <div className="flex-shrink-0" style={{ width: "128px" }}>
                      <div className="overflow-hidden px-4">
                        <OverflowDisplayTooltip
                          content={getEntityName(item.type)}
                          className="block text-xs text-gray-700"
                        >
                          {getEntityName(item.type)}
                        </OverflowDisplayTooltip>
                      </div>
                    </div>
                  )}

                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="overflow-hidden px-4">
                      <UserAttribution email={item.created_by_email} />
                    </div>
                  </div>

                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="overflow-hidden px-4">
                      <OverflowDisplayTooltip
                        content={formatDate(item.created_at)}
                        className="block text-xs text-gray-500"
                      >
                        {formatDate(item.created_at)}
                      </OverflowDisplayTooltip>
                    </div>
                  </div>

                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="overflow-hidden px-4">
                      <OverflowDisplayTooltip
                        content={formatDate(item.updated_at || item.created_at)}
                        className="block text-xs text-gray-500"
                      >
                        {formatDate(item.updated_at || item.created_at)}
                      </OverflowDisplayTooltip>
                    </div>
                  </div>

                  {trailingActions}
                </Link>
              )}
            </div>
          </ContextMenu>

          <ExpandedContent
            item={item}
            isExpanded={isExpanded}
            depth={depth}
            isLastChild={isLastChild}
            isCompact={isCompact}
            onItemClick={onItemClick}
          />
        </div>
      </DraggableItem>
    );
  },
);

ListItem.displayName = "ListItem";

export default ListItem;
